/**
 * Wishlist page ("My Favorites") rendering, sort/filter, and add-to-cart.
 *
 * Reads items from window.SgWishlist (localStorage-backed) and renders one
 * .wishlist-card per item into [data-wishlist-grid]. Re-renders whenever the
 * wishlist:change event fires (e.g. a heart toggle on a PLP/PDP in another
 * tab, or removing an item from this page).
 *
 * Depends on:
 * - window.SgWishlist (assets/wishlist.js): getAll/remove, wishlist:change event
 * - window.SgCart (assets/cart-drawer.js): add, open
 * - window.SgWishlistStrings (inlined in sections/new-wishlist.liquid): translated strings
 */
(function () {
  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    return (cents / 100).toLocaleString(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: currency
    });
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function renderCard(item) {
    var soldOutBadge = item.available === false
      ? '<span class="wishlist-card__badge">' + escapeHtml(window.SgWishlistStrings.soldOut) + '</span>'
      : '';

    var addLabel = item.available === false
      ? window.SgWishlistStrings.soldOut
      : window.SgWishlistStrings.addToCart + ' ' + formatMoney(item.price);

    return (
      '<div class="wishlist-card" data-wishlist-card data-product-id="' + escapeHtml(item.id) + '" ' +
      'data-title="' + escapeHtml(item.title) + '" data-price="' + escapeHtml(item.price) + '" ' +
      'data-available="' + escapeHtml(item.available) + '">' +
        '<a href="/products/' + escapeHtml(item.handle) + '" class="wishlist-card__media">' +
          '<span class="wishlist-card__media-bg" aria-hidden="true"></span>' +
          '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy" class="wishlist-card__image">' +
          '<span class="wishlist-card__scrim" aria-hidden="true"></span>' +
          (item.available === false ? '<div class="wishlist-card__badges">' + soldOutBadge + '</div>' : '') +
        '</a>' +
        '<button type="button" class="wishlist-card__heart" data-wishlist-remove aria-label="' + escapeHtml(window.SgWishlistStrings.remove) + '">' +
          window.SgWishlistStrings.heartIcon +
        '</button>' +
        '<div class="wishlist-card__content">' +
          '<h3 class="wishlist-card__title">' + escapeHtml(item.title) + '</h3>' +
          '<button type="button" class="wishlist-card__add" data-wishlist-add data-handle="' + escapeHtml(item.handle) + '" ' + (item.available === false ? 'disabled' : '') + '>' +
            escapeHtml(addLabel) +
          '</button>' +
        '</div>' +
      '</div>'
    );
  }

  function getFilteredItems(root) {
    var items = window.SgWishlist.getAll().slice();
    var availability = root.querySelector('[data-wishlist-availability]').value;
    var sort = root.querySelector('[data-wishlist-sort]').value;
    var priceEl = root.querySelector('[data-wishlist-price]');
    var price = priceEl ? priceEl.value : 'all';

    if (availability === 'available') {
      items = items.filter(function (item) {
        return item.available !== false;
      });
    }

    if (price === 'under-10') {
      items = items.filter(function (item) {
        return item.price < 1000;
      });
    } else if (price === 'over-10') {
      items = items.filter(function (item) {
        return item.price >= 1000;
      });
    }

    switch (sort) {
      case 'title-asc':
        items.sort(function (a, b) { return a.title.localeCompare(b.title); });
        break;
      case 'title-desc':
        items.sort(function (a, b) { return b.title.localeCompare(a.title); });
        break;
      case 'price-asc':
        items.sort(function (a, b) { return a.price - b.price; });
        break;
      case 'price-desc':
        items.sort(function (a, b) { return b.price - a.price; });
        break;
      default:
        break;
    }

    return items;
  }

  function render(root) {
    var grid = root.querySelector('[data-wishlist-grid]');
    var empty = root.querySelector('[data-wishlist-empty]');
    var toolbar = root.querySelector('[data-wishlist-toolbar]');
    var countEl = root.querySelector('[data-wishlist-count]');

    var allItems = window.SgWishlist.getAll();
    countEl.textContent = window.SgWishlistStrings.itemsSaved(allItems.length);

    if (allItems.length === 0) {
      grid.innerHTML = '';
      grid.hidden = true;
      toolbar.hidden = true;
      empty.hidden = false;
      return;
    }

    grid.hidden = false;
    toolbar.hidden = false;
    empty.hidden = true;

    var items = getFilteredItems(root);
    grid.innerHTML = items.map(renderCard).join('');
  }

  function addToCart(handle, button) {
    var originalText = button.textContent;
    button.disabled = true;

    fetch('/products/' + handle + '.js')
      .then(function (response) { return response.json(); })
      .then(function (product) {
        var variant = product.variants.find(function (v) { return v.available; });
        if (!variant) return Promise.reject(new Error('No available variant'));
        return window.SgCart.add(variant.id, 1);
      })
      .then(function () {
        window.SgCart.open();
      })
      .catch(function () {
        button.textContent = window.SgWishlistStrings.error;
      })
      .finally(function () {
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  function addAllToCart(root) {
    var items = window.SgWishlist.getAll().filter(function (item) {
      return item.available !== false;
    });

    var chain = Promise.resolve();
    items.forEach(function (item) {
      chain = chain
        .then(function () {
          return fetch('/products/' + item.handle + '.js').then(function (response) { return response.json(); });
        })
        .then(function (product) {
          var variant = product.variants.find(function (v) { return v.available; });
          if (!variant) return Promise.resolve();
          return window.SgCart.add(variant.id, 1);
        });
    });

    chain.then(function () {
      window.SgCart.open();
    });
  }

  function init() {
    var root = document.querySelector('.wishlist-page');
    if (!root || !window.SgWishlist || !window.SgCart) return;

    render(root);

    root.querySelector('[data-wishlist-sort]').addEventListener('change', function () {
      render(root);
    });

    root.querySelector('[data-wishlist-availability]').addEventListener('change', function () {
      render(root);
    });

    var priceEl = root.querySelector('[data-wishlist-price]');
    if (priceEl) {
      priceEl.addEventListener('change', function () {
        render(root);
      });
    }

    root.querySelector('[data-wishlist-clear]').addEventListener('click', function () {
      window.SgWishlist.getAll().forEach(function (item) {
        window.SgWishlist.remove(item.id);
      });
      render(root);
    });

    root.querySelector('[data-wishlist-add-all]').addEventListener('click', function () {
      addAllToCart(root);
    });

    root.querySelector('[data-wishlist-grid]').addEventListener('click', function (event) {
      var removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        var card = removeButton.closest('[data-wishlist-card]');
        window.SgWishlist.remove(card.dataset.productId);
        render(root);
        return;
      }

      var addButton = event.target.closest('[data-wishlist-add]');
      if (addButton) {
        addToCart(addButton.dataset.handle, addButton);
      }
    });

    document.addEventListener('wishlist:change', function () {
      render(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
